import { useState } from 'react'
import Box from '@mui/joy/Box'
import CircularProgress from '@mui/joy/CircularProgress'
import Typography from '@mui/joy/Typography'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
    isAdditionalPaymentCheckoutFlow,
    type CheckoutFlow,
} from '@/checkout-paths'
import type { CheckoutSuccessPageDataPayload } from '@/types/reservation-checkout'
import ReservationSuccessImage from '@/images/reservation-success.webp'
import { searchSubmissionAtom } from '@/state'
import { RETURN_IN_HISTORY_PARAM_KEY, ROUTE_SEGMENTS } from '@/pwa/constants'
import CheckoutSuccessAppBanner from '../app-banner'
import CheckoutSuccessNewsletterBanner from '../newsletter-banner'
import CheckoutSuccessReservationSummary from '../reservation-summary'
import type { CheckoutSuccessHeroSecondaryAction } from '../success-hero'
import CheckoutSuccessHero from '../success-hero'
import {
    getCheckoutSuccessRepeatSearchSubmissions,
    getCheckoutSuccessRepeatWebLinks,
    mapCheckoutSuccessPageDataToSummary,
} from '../summary-utils'
import { CheckoutSuccessStateCard } from './states'
import {
    getCheckoutSuccessFallbackStateContent,
    getCheckoutSuccessHeroContent,
} from './view-model'

type SuccessPageDataQueryState = {
    status: 'pending' | 'error' | 'success'
    data?: CheckoutSuccessPageDataPayload
    error?: Error | null
}

export function CheckoutSuccessFallbackStatusContent({
    paymentStatus,
    isPwa,
    isLoggedIn,
    locale,
    checkoutPath,
    checkoutFlow,
    returnTo,
}: {
    paymentStatus: string
    isPwa: boolean
    isLoggedIn: boolean
    locale: string
    checkoutPath: string
    checkoutFlow: CheckoutFlow
    returnTo: string | null
}) {
    const { t } = useTranslation()
    const stateContent = getCheckoutSuccessFallbackStateContent({
        paymentStatus,
        isPwa,
        isLoggedIn,
        locale,
        checkoutPath,
        checkoutFlow,
        returnTo,
        t,
    })

    return <CheckoutSuccessStateCard {...stateContent} />
}

export function CheckoutSuccessRichContent({
    paymentStatus,
    successPageDataQuery,
    isPwa,
    isLoggedIn,
    locale,
    checkoutPath,
    checkoutFlow,
    returnTo,
}: {
    paymentStatus: string
    successPageDataQuery: SuccessPageDataQueryState
    isPwa: boolean
    isLoggedIn: boolean
    locale: string
    checkoutPath: string
    checkoutFlow: CheckoutFlow
    returnTo: string | null
}) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const setSearchSubmission = useSetAtom(searchSubmissionAtom)
    const [
        newsletterSubscribedInCurrentRender,
        setNewsletterSubscribedInCurrentRender,
    ] = useState(false)
    const heroContent = getCheckoutSuccessHeroContent({
        paymentStatus,
        isPwa,
        isLoggedIn,
        locale,
        checkoutPath,
        checkoutFlow,
        returnTo,
        hasInstallationToken: successPageDataQuery.data?.reservation.has_installation_token ?? false,
        sendSms: successPageDataQuery.data?.reservation.send_sms ?? false,
        t,
    })
    const summaryLayoutSx = {
        width: '100%',
        gridColumn: {
            lg: 2,
        },
        gridRow: {
            lg: isPwa ? 'auto' : '1 / span 2',
        },
    } as const
    const shouldShowNewsletterBanner = paymentStatus === 'successful'
        && successPageDataQuery.status === 'success'
        && (
            successPageDataQuery.data?.reservation.newsletter_subscription_status === 'not_subscribed'
            || newsletterSubscribedInCurrentRender
        )
        && !isAdditionalPaymentCheckoutFlow(checkoutFlow)
    let secondaryActions: CheckoutSuccessHeroSecondaryAction[] = []

    if (
        !isAdditionalPaymentCheckoutFlow(checkoutFlow)
        &&
        paymentStatus === 'successful'
        && successPageDataQuery.status === 'success'
        && successPageDataQuery.data?.garage
    ) {
        if (isPwa) {
            const repeatSearchSubmissions = getCheckoutSuccessRepeatSearchSubmissions(successPageDataQuery.data)
            const garageId = successPageDataQuery.data.garage.id

            secondaryActions = [
                {
                    label: t('reservations.table.bookAgain'),
                    onClick: () => {
                        setSearchSubmission(repeatSearchSubmissions.sameTerm)
                        navigate(`/pwa/${ROUTE_SEGMENTS.garage}/${garageId}?${RETURN_IN_HISTORY_PARAM_KEY}=true`)
                    },
                },
                {
                    label: t('reservations.table.bookNextDay'),
                    onClick: () => {
                        setSearchSubmission(repeatSearchSubmissions.nextDay)
                        navigate(`/pwa/${ROUTE_SEGMENTS.garage}/${garageId}?${RETURN_IN_HISTORY_PARAM_KEY}=true`)
                    },
                },
            ]
        } else {
            const repeatWebLinks = getCheckoutSuccessRepeatWebLinks(successPageDataQuery.data, locale)

            if (repeatWebLinks) {
                secondaryActions = [
                    {
                        label: t('reservations.table.bookAgain'),
                        href: repeatWebLinks.sameTermHref,
                    },
                    {
                        label: t('reservations.table.bookNextDay'),
                        href: repeatWebLinks.nextDayHref,
                    },
                ]
            }
        }
    }

    return (
        <Box sx={{
            px: 2,
            py: {
                xs: 2,
                sm: 3,
            },
        }}>
            <Box sx={{
                width: '100%',
                maxWidth: {
                    xs: 800,
                    lg: 1400,
                },
                mx: 'auto',
                display: 'grid',
                gridTemplateColumns: {
                    xs: 'minmax(0, 1fr)',
                    lg: 'minmax(0, 1.35fr) minmax(320px, 0.65fr)',
                },
                gap: 3,
                alignItems: 'start',
            }}>
                <CheckoutSuccessHero
                    imageSrc={ReservationSuccessImage}
                    imageAlt={heroContent.imageAlt}
                    title={heroContent.title}
                    text={heroContent.text}
                    actionHref={heroContent.actionHref}
                    actionLabel={heroContent.actionLabel}
                    secondaryActions={secondaryActions}
                />

                {successPageDataQuery.status === 'pending' ? (
                    <Box sx={summaryLayoutSx}>
                        <Typography
                            level="title-md"
                            component="h2"
                            sx={{
                                color: '#0B0B0B',
                                fontWeight: 600,
                                mb: 1.25,
                                textAlign: {
                                    xs: 'left',
                                    lg: 'center',
                                },
                            }}
                        >
                            {t('success.summary.title')}
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 280,
                            backgroundColor: 'common.white',
                            borderRadius: '14px',
                            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
                        }}>
                            <CircularProgress />
                        </Box>
                    </Box>
                ) : null}

                {successPageDataQuery.status === 'error' ? (
                    <Box sx={{
                        ...summaryLayoutSx,
                        backgroundColor: 'common.white',
                        borderRadius: '14px',
                        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
                        p: 3,
                        textAlign: 'center',
                    }}>
                        <Typography
                            level="title-md"
                            component="h2"
                            sx={{
                                color: '#0B0B0B',
                                fontWeight: 600,
                                mb: 1,
                                textAlign: {
                                    xs: 'left',
                                    lg: 'center',
                                },
                            }}
                        >
                            {t('success.summary.title')}
                        </Typography>
                        <Typography sx={{ color: '#484848' }}>
                            {successPageDataQuery.error?.message}
                        </Typography>
                    </Box>
                ) : null}

                {successPageDataQuery.status === 'success' && successPageDataQuery.data ? (
                    <Box sx={summaryLayoutSx}>
                        <CheckoutSuccessReservationSummary
                            summary={mapCheckoutSuccessPageDataToSummary(successPageDataQuery.data, t)}
                        />
                    </Box>
                ) : null}

                {shouldShowNewsletterBanner ? (
                    <Box sx={{
                        gridColumn: {
                            lg: 1,
                        },
                    }}>
                        <CheckoutSuccessNewsletterBanner
                            locale={locale}
                            onSubscribed={() => setNewsletterSubscribedInCurrentRender(true)}
                        />
                    </Box>
                ) : null}

                {!isPwa ? (
                    <Box sx={{
                        gridColumn: {
                            lg: 1,
                        },
                    }}>
                        <CheckoutSuccessAppBanner locale={locale} />
                    </Box>
                ) : null}
            </Box>
        </Box>
    )
}
